/**
 * @file button.tsx
 * @description Кнопка-примитив (shadcn base-nova адаптация под Chaiberi
 * Editorial). Phase08.5.
 *
 * Варианты:
 *  - default — primary gradient (135deg, olive → light olive)
 *  - outline — прозрачный с muted hover (No-Line Rule: используем ring вместо border при необходимости)
 *  - secondary — surface-low (бежевый)
 *  - ghost — без фона, hover ставит surface-low
 *  - destructive — красный
 *  - link — text с underline
 *
 * phase19.29 — варианты «цвет = смысл» (в теме Commora у каждого свой цвет,
 * в теме магазина пока не используются):
 *  - brand — главное действие экрана (градиент бренда; одно на экран);
 *  - soft — ненавязчивое действие (мягкая синяя: «Добавить экземпляр»,
 *    «Повторить», фильтры);
 *  - success — включить, подтвердить, проверить (мягкая зелёная);
 *  - warning — действие с последствиями, но обратимое (мягкая янтарная);
 *  - destructive — опасное действие на странице (мягкая красная);
 *  - destructive-solid — окончательное подтверждение удаления/отзыва в окне;
 *  - accent — особые/AI-функции (сплошная фиолетовая; пока не применяется).
 * Вариант и размер дублируются в `data-variant` / `data-size`: по ним тема
 * Commora задаёт два размера (обычная / компактная) и цвета, не меняя
 * классы — кабинет магазина остаётся прежним.
 *
 * `loading` — «идёт выполнение»: кнопка блокируется, слева крутится значок,
 * собственный значок кнопки на это время прячется.
 *
 * Размеры: xs / sm / default / lg + icon-варианты.
 */

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary-gradient text-on-primary shadow-sm hover:shadow active:translate-y-px",
        brand:
          "bg-primary-gradient brand-cta text-on-primary shadow-sm hover:shadow active:translate-y-px",
        soft: "bg-accent text-accent-foreground hover:bg-accent/80",
        success:
          "bg-success text-on-primary shadow-sm hover:shadow active:translate-y-px",
        warning:
          "bg-warning text-on-primary shadow-sm hover:shadow active:translate-y-px",
        accent:
          "bg-primary-end text-on-primary shadow-sm hover:shadow active:translate-y-px",
        outline:
          "bg-transparent text-on-surface hover:bg-surface-low [box-shadow:inset_0_0_0_1px_var(--outline-variant)]",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-surface-low/80",
        ghost:
          "bg-transparent text-on-surface hover:bg-surface-low",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/15",
        "destructive-solid":
          "bg-destructive text-on-primary shadow-sm hover:shadow active:translate-y-px",
        link: "text-primary underline-offset-4 hover:underline px-0 h-auto",
      },
      size: {
        default: "h-9 px-4",
        xs: "h-7 px-2 text-xs rounded-md gap-1 [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 px-3 text-sm rounded-md",
        lg: "h-10 px-6",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-xs": "size-7 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Идёт выполнение: кнопка заблокирована, показан индикатор. */
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
    ref,
  ) => {
    const Comp: React.ElementType = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        data-slot="button"
        data-variant={variant ?? "default"}
        data-size={size ?? "default"}
        // Атрибут только когда идёт выполнение — иначе DOM как раньше.
        data-loading={loading ? "" : undefined}
        aria-busy={loading || undefined}
        disabled={disabled || loading || undefined}
        className={cn(
          buttonVariants({ variant, size }),
          loading && "[&>svg:not([data-spinner])]:hidden",
          className,
        )}
        {...props}
      >
        {/* Slot требует ровно одного ребёнка — индикатор только у обычной кнопки. */}
        {asChild ? (
          children
        ) : (
          <>
            {loading ? <Loader2 data-spinner="" className="animate-spin" aria-hidden /> : null}
            {children}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
